import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, AlertTriangle } from "lucide-react";
import { api, errMsg, download, eur, dt, today } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet } from "@/components/coge/CogeViews";

const TH = ({ children, r }) => <th className={`whitespace-nowrap px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider ${r ? "text-right" : "text-left"}`}>{children}</th>;
const TD = ({ children, r, className = "" }) => <td className={`px-3 py-2 ${r ? "text-right font-mono tabular-nums" : ""} ${className}`}>{children}</td>;
const ANNI = Array.from({ length: 6 }, (_, i) => String(Number(today().slice(0, 4)) - i));

const Bar = ({ anno, setAnno, onPdf, children, testid }) => (
  <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <label className="text-xs font-medium text-slate-600">Anno
      <Select value={anno} onValueChange={setAnno}><SelectTrigger className="mt-1 w-32" data-testid={`${testid}-anno`}><SelectValue /></SelectTrigger>
        <SelectContent>{ANNI.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
    </label>
    {children}
    <Button variant="outline" className="ml-auto" data-testid={`${testid}-pdf`} onClick={onPdf}><FileDown className="mr-2 h-4 w-4" />Stampa PDF</Button>
  </div>
);

const useYear = (url, params) => {
  const [rep, setRep] = useState(null);
  const key = JSON.stringify(params);
  const load = useCallback(() => { setRep(null); api.get(url, { params: JSON.parse(key) }).then((r) => setRep(r.data)).catch((e) => toast.error(errMsg(e))); }, [url, key]);
  useEffect(() => { load(); }, [load]);
  return rep;
};

export const Registri = ({ base, regime }) => {
  const [anno, setAnno] = useState(today().slice(0, 4));
  const forf = regime === "forfettaria";
  const [tipo, setTipo] = useState("vendite");
  const rep = useYear(`${base}/registri`, { anno, tipo });
  return (
    <div>
      <Bar testid="reg" anno={anno} setAnno={setAnno} onPdf={() => download(`${base}/registri`, { anno, tipo, pdf: true }).catch((e) => toast.error(e.message))}>
        {!forf && <label className="text-xs font-medium text-slate-600">Registro
          <Select value={tipo} onValueChange={setTipo}><SelectTrigger className="mt-1 w-48" data-testid="reg-tipo"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="vendite">IVA vendite</SelectItem><SelectItem value="acquisti">IVA acquisti</SelectItem></SelectContent></Select>
        </label>}
      </Bar>
      {rep && (
        <Sheet testid="reg-table" head={<><TH>Data</TH><TH>Numero</TH><TH>Controparte</TH><TH>P.IVA / C.F.</TH><TH r>Imponibile</TH>{!forf && <TH r>IVA</TH>}<TH r>Totale</TH>{forf && <TH>Incassata il</TH>}</>}
          foot={<tr><TD /><TD /><TD>Totali anno ({rep.items.length} {rep.items.length === 1 ? "documento" : "documenti"})</TD><TD /><TD r>{eur(rep.imponibile)}</TD>{!forf && <TD r>{eur(rep.iva)}</TD>}<TD r>{eur(rep.totale)}</TD>{forf && <TD />}</tr>}>
          {(rep.trimestri || []).map((tq) => (
            <Fragment key={tq.trimestre}>
              <tr className="bg-sky-50/70" data-testid={`reg-trim-${tq.trimestre}`}><TD /><TD /><TD className="font-bold uppercase tracking-wide text-sky-800">{tq.label}</TD><TD /><TD />{!forf && <TD />}<TD />{forf && <TD />}</tr>
              {rep.items.filter((r) => r.trimestre === tq.trimestre).map((r) => (
            <tr key={r.id} data-testid={`reg-row-${r.id}`}>
              <TD className="whitespace-nowrap">{dt(r.data)}</TD><TD className="font-mono text-xs">{r.numero}{r.tipo === "TD04" && <span className="ml-1 text-rose-600">NC</span>}</TD>
              <TD className="font-medium text-slate-900">{r.controparte}</TD><TD className="whitespace-nowrap font-mono text-xs text-slate-500">{r.piva}</TD>
              <TD r>{eur(r.imponibile)}</TD>{!forf && <TD r>{eur(r.iva)}</TD>}<TD r className="font-semibold">{eur(r.totale)}</TD>
              {forf && <TD className="text-sm">{r.data_pagamento ? dt(r.data_pagamento) : <span className="text-amber-700">da incassare</span>}</TD>}
            </tr>
              ))}
              <tr className="bg-slate-50 font-semibold" data-testid={`reg-trim-tot-${tq.trimestre}`}><TD /><TD /><TD>Totale {tq.label} ({tq.documenti} doc.)</TD><TD /><TD r>{eur(tq.imponibile)}</TD>{!forf && <TD r>{eur(tq.iva)}</TD>}<TD r>{eur(tq.totale)}</TD>{forf && <TD />}</tr>
              {!forf && tq.riepilogo.map((a) => <tr key={a.aliquota} className="text-xs text-slate-500"><TD /><TD /><TD className="pl-8">Aliquota {a.aliquota}</TD><TD /><TD r>{eur(a.imponibile)}</TD><TD r>{eur(a.iva)}</TD><TD /></tr>)}
            </Fragment>
          ))}
        </Sheet>
      )}
      {rep && !forf && rep.riepilogo.length > 0 && (
        <div className="mt-4 max-w-xl"><Sheet testid="reg-riepilogo" head={<><TH>Riepilogo annuale aliquota / natura</TH><TH r>Imponibile</TH><TH r>Imposta</TH></>}>
          {rep.riepilogo.map((r) => <tr key={r.aliquota}><TD>{r.aliquota}</TD><TD r>{eur(r.imponibile)}</TD><TD r>{eur(r.iva)}</TD></tr>)}
        </Sheet></div>
      )}
    </div>
  );
};

const Limite = ({ l }) => (
  <div className="mb-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="forf-limite">
    <div className="flex justify-between text-sm"><span className="font-semibold text-slate-900">Limite ricavi regime forfettario</span><span className="font-mono">{eur(l.ricavi)} / {eur(l.limite)}</span></div>
    <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${l.percentuale >= 100 ? "bg-rose-600" : l.percentuale >= 80 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, l.percentuale)}%` }} />
    </div>
    <div className="mt-1 text-xs text-slate-500">{String(l.percentuale).replace(".", ",")}% del limite</div>
  </div>
);

export const Reddito = ({ base }) => {
  const [anno, setAnno] = useState(today().slice(0, 4));
  const rep = useYear(`${base}/reddito`, { anno });
  return (
    <div>
      <Bar testid="red" anno={anno} setAnno={setAnno} onPdf={() => download(`${base}/reddito`, { anno, pdf: true }).catch((e) => toast.error(e.message))}>
        {rep && <div className="text-sm"><div className="text-xs uppercase tracking-wider text-slate-500">Regime</div><div className="font-semibold text-slate-900" data-testid="red-regime">{rep.regime_nome}</div></div>}
      </Bar>
      {rep?.avvisi.map((a) => <div key={a} className="mb-3 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="red-avviso"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{a}</div>)}
      {rep?.limite && <Limite l={rep.limite} />}
      {rep && (
        <Sheet testid="red-table" head={<><TH>Sezione</TH><TH>Voce</TH><TH r>Importo</TH></>}>
          {rep.righe.map((r, i) => (
            <tr key={i} className={r.tot ? "bg-slate-50 font-semibold" : ""} data-testid={`red-row-${i}`}>
              <TD className="text-xs uppercase tracking-wider text-slate-500">{r.sez}</TD>
              <TD className={r.info ? "pl-8 italic text-slate-500" : "text-slate-900"}>{r.voce}</TD>
              <TD r className={r.tot ? "text-slate-900" : ""}>{r.sez === "Attività" ? "" : eur(r.importo)}</TD>
            </tr>
          ))}
        </Sheet>
      )}
      <p className="mt-3 text-xs text-slate-500">Stima gestionale calcolata da fatture, corrispettivi e prima nota: non sostituisce la dichiarazione dei redditi. Coefficienti e aliquote vanno verificati con il commercialista.</p>
    </div>
  );
};
